'use client';

import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@kit/shared/currency';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

import type { ClassSeasonQuote } from '~/lib/class-seasons/class-season-quote';

/**
 * Season price as quoted by the server: the full season price struck through
 * next to the price for the sessions left when the season is prorated, or a
 * single price otherwise. Renders values verbatim — no price math here.
 */
export function SeasonPriceBlock({
  quote,
  className,
}: {
  quote: Pick<
    ClassSeasonQuote,
    | 'prorated'
    | 'total_sessions'
    | 'remaining_sessions'
    | 'price'
    | 'full_price'
  >;
  className?: string;
}) {
  const { t, i18n } = useTranslation('bookings');
  const currency = useTenantCurrency();

  const format = (amount: number) =>
    formatCurrency(amount, currency, i18n.language, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div
      className={cn('grid grid-cols-[1fr_auto] gap-x-6 gap-y-1', className)}
      data-test="season-price-block"
    >
      {quote.prorated ? (
        <>
          <span className="text-muted-foreground text-sm line-through">
            {t('season.allSessions', { count: quote.total_sessions })}
          </span>
          <span className="text-muted-foreground inline-flex items-center justify-end gap-1 text-sm line-through">
            <CurrencyIcon className="h-3 w-3" />
            {format(quote.full_price)}
          </span>
        </>
      ) : null}
      <span className="text-sm font-medium">
        {t('season.sessionsLeft', { count: quote.remaining_sessions })}
      </span>
      <span className="inline-flex items-center justify-end gap-1 text-lg font-semibold">
        <CurrencyIcon className="h-4 w-4" />
        {format(quote.price)}
      </span>
    </div>
  );
}
